import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "react-toastify";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const useAdd = (
  queryKey,
  token = null,
  customSuccessMessage = null,
  customErrorMessage = null
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [resetForm, setResetForm] = useState(false);
  const [responseData, setResponseData] = useState(null);

  // Default messages
  const defaultSuccessMessage = t("use_add.dataAddedSuccess");
  const defaultErrorMessage = t("use_add.addFailed");

  const successMessage = customSuccessMessage || defaultSuccessMessage;
  const errorMessage = customErrorMessage || defaultErrorMessage;

  const addMutation = useMutation({
    mutationFn: async (data) => {
      setLoading(true);

      // Initialize headers object
      const headers = {};
      const isMultipart = data instanceof FormData;

      // Add Authorization header only if the token is valid
      if (token && typeof token === "string" && token.trim().length > 0) {
        headers.Authorization = `Token ${token.trim()}`;
      }

      if (isMultipart) {
        // Let the browser set multipart boundaries for FormData uploads.
        headers["Content-Type"] = false;
      }

      const response = await axios.post(
        `${queryKey}/`,
        data,
        { headers, skipGlobalErrorHandler: true }
      );
      setResponseData(response.data);
      return response.data;
    },
    onSuccess: () => {
      setLoading(false);
      toast.success(successMessage, {
        position: "top-right",
        autoClose: 3000,
      });
      setResetForm(true);

      // Reset the form after a short delay
      setTimeout(() => {
        setResetForm(false);
      }, 100);

      // Invalidate the query to refresh the data (TanStack Query v4+ requires queryKey to be an array)
      const key = Array.isArray(queryKey) ? queryKey : [queryKey];
      queryClient.invalidateQueries({ queryKey: key });
    },
    onError: (error) => {
      setLoading(false);

      // Handle specific backend errors if available
      const backendErrors = error.response?.data;
      if (backendErrors && typeof backendErrors === "object") {
        Object.keys(backendErrors).forEach((key) => {
          const errorMsg = Array.isArray(backendErrors[key])
            ? backendErrors[key].join(", ")
            : backendErrors[key];
          toast.error(`${key}: ${errorMsg}`, {
            position: "top-right",
            autoClose: 3000,
          });
        });
      } else {
        // If no specific backend error, show generic error message
        toast.error(errorMessage || `Error: ${error.message}`, {
          position: "top-right",
          autoClose: 3000,
        });
      }
    },
  });

  const handleAdd = (data) => {
    addMutation.mutate(data);
  };

  return {
    handleAdd,
    resetForm,
    loading,
    responseData,
  };
};

export default useAdd;
